import React from 'react';
import { cn } from '../utils/cn';
import { AlertCircle, CheckCircle, Info, X, AlertTriangle } from 'lucide-react';

/**
 * Alert Component - Feedback
 * 
 * @param {Object} props
 * @param {string} props.variant - 'success' | 'error' | 'warning' | 'info'
 * @param {string} props.title - Alert title
 * @param {React.ReactNode} props.children - Alert content
 * @param {boolean} props.dismissible - Show dismiss button
 * @param {Function} props.onDismiss - Dismiss handler
 */
const Alert = ({
  variant = 'info',
  title,
  children,
  dismissible = false,
  onDismiss,
  className = '',
  ...props
}) => {
  const variants = {
    success: {
      bg: 'bg-success-200 dark:bg-success-900',
      border: 'border-success-400 dark:border-success-700',
      text: 'text-success-900 dark:text-success-50',
      icon: CheckCircle,
    },
    error: {
      bg: 'bg-error-200 dark:bg-error-900',
      border: 'border-error-400 dark:border-error-700',
      text: 'text-error-900 dark:text-error-50',
      icon: AlertCircle,
    },
    warning: {
      bg: 'bg-warning-200 dark:bg-warning-900',
      border: 'border-warning-400 dark:border-warning-700',
      text: 'text-warning-900 dark:text-warning-50',
      icon: AlertTriangle,
    },
    info: {
      bg: 'bg-info-200 dark:bg-info-900',
      border: 'border-info-400 dark:border-info-700',
      text: 'text-info-900 dark:text-info-50',
      icon: Info,
    },
  };

  const variantStyles = variants[variant];
  const Icon = variantStyles.icon;

  return (
    <div
      className={cn(
        'rounded-xl border-2 p-4 shadow-xl',
        variantStyles.bg,
        variantStyles.border,
        'min-w-[320px] max-w-full',
        className
      )}
      role="alert"
      {...props}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', variantStyles.text)} />
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={cn('font-semibold mb-1 text-sm', variantStyles.text)}>
              {title}
            </h4>
          )}
          <div className={cn('text-sm break-words', variantStyles.text)}>{children}</div>
        </div>
        {dismissible && (
          <button
            onClick={onDismiss}
            className={cn(
              'flex-shrink-0 hover:opacity-70 transition-opacity p-1 -mt-1 -mr-1',
              variantStyles.text
            )}
            aria-label="Dismiss alert"
            type="button"
          >
            <X size={18} className="flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
